import { type MouseEvent, useCallback, useEffect, useState } from 'react';
import { flushSync } from 'react-dom';

import { Box, Button, Card, Icon, Tag, Text } from '@zillow/constellation-10';
import { IconDoorOpenFilled } from '@zillow/constellation-icons';

import { type MultiplexBuildingSessionOverviewUnitStatusWebDto } from '@zg-rentals/ts-rental-manager-api-beta-client';

export type UnitItemProps = {
  unitName: string;
  unitType: string;
  status: MultiplexBuildingSessionOverviewUnitStatusWebDto;
};

const UnitItem = ({ unitName, unitType, status }: UnitItemProps) => {
  const ctaUrl = status.buttonUrl?.trim() ?? '';
  const hasCtaUrl = Boolean(ctaUrl);
  const [isNavigationPending, setIsNavigationPending] = useState(false);

  useEffect(() => {
    setIsNavigationPending(false);
  }, [ctaUrl]);

  const handleCtaClick = useCallback(
    (event: MouseEvent<HTMLAnchorElement>) => {
      if (isNavigationPending) {
        event.preventDefault();
        return;
      }

      const leaveDefaultBrowserNavigation =
        event.metaKey || event.ctrlKey || event.shiftKey || event.altKey || event.button !== 0;

      if (leaveDefaultBrowserNavigation) {
        return;
      }

      event.preventDefault();
      /**
       * TODO: use client-side routing instead of window.location.assign on nextjs migration
       *
       * Full-page navigation: flushSync keeps the CTA disabled until unload. After the Next.js
       * migration, prefer client-side routing (e.g. next/link or the app router) so pending UI can
       * follow router transition state instead of location.assign + flushSync.
       */
      flushSync(() => {
        setIsNavigationPending(true);
      });
      window.location.assign(ctaUrl);
    },
    [ctaUrl, isNavigationPending],
  );

  return (
    <Card
      data-testid="UnitItem"
      tone="neutral"
      elevated={false}
      css={{
        display: 'flex',
        flexDirection: 'row',
        alignItems: 'center',
        justifyContent: 'space-between',
        gap: '200',
        width: '100%',
        paddingBlock: '300',
        paddingInline: '400',
      }}
    >
      <Box css={{ display: 'flex', flexDirection: 'row', alignItems: 'center', gap: '300', minWidth: 0, flex: 1 }}>
        <Box css={{ flexShrink: 0 }} aria-hidden={true}>
          <Icon size="lg" css={{ color: 'icon.neutral' }} render={<IconDoorOpenFilled />} />
        </Box>
        <Box css={{ display: 'flex', flexDirection: 'column', gap: '25', minWidth: 0 }}>
          <Text textStyle="body-bold" css={{ color: 'text.default' }}>
            {unitName}
          </Text>
          <Text textStyle="body-sm" css={{ color: 'text.subtle' }}>
            {unitType}
          </Text>
        </Box>
      </Box>

      <Box
        css={{
          display: 'flex',
          flexDirection: 'row',
          alignItems: 'center',
          gap: '200',
          flexShrink: 0,
        }}
      >
        <Tag size="sm" tone="gray">
          {status.displayStatus}
        </Tag>

        {hasCtaUrl ? (
          <Button asChild tone="brand" emphasis="filled" size="sm" disabled={isNavigationPending}>
            <a href={ctaUrl} onClick={handleCtaClick} aria-busy={isNavigationPending}>
              {status.buttonText}
            </a>
          </Button>
        ) : (
          <Button type="button" tone="brand" emphasis="filled" size="sm" disabled={true}>
            {status.buttonText}
          </Button>
        )}
      </Box>
    </Card>
  );
};

export default UnitItem;
